import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils

import _ from 'lodash';
import { storeActions } from 'redux/stores/storeSlice';
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
import { USER_ROLE, BASE_URL } from '../config';
// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  signupData: null,
  userActivity: null
};

// const baseURL = 'https://service-plug-apis.herokuapp.com';
const baseURL = BASE_URL;

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  TOGGLE_INITIALIZE: (state, action) => ({
    ...state,
    isInitialized: action.payload
  }),
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      signupData: null
    };
  },

  USER_ACTIVITY: (state, action) => {
    const { userActivity } = action.payload;
    return {
      ...state,
      userActivity
    };
  }
};

const defaultUser = { displayName: 'Serviceplug Admin' };

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  sellerRegister: () => Promise.resolve(),
  sendOtp: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  verifyOtp: () => Promise.resolve(),
  getUser: () => Promise.resolve(),
  markUserVerified: () => Promise.resolve(),
  updateSignupData: () => {},
  updateUserActivity: () => {},
  setBackLoading: () => Promise.resolve(),
  selectStore: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (!_.isEmpty(accessToken)) {
          const user = await getUser();
          setSession(accessToken);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (userName, password) => {
    const response = await axios.post(`${baseURL}/admin/login`, {
      userName,
      password
      // role: role || USER_ROLE
    });
    if (response?.data) {
      const { token, user } = response.data;

      // delete user.loginToken;

      setSession(token);
      dispatch({
        type: 'LOGIN',
        payload: {
          user
        }
      });

      return user;
    }
    return null;
  };

  const selectStore = async (store) => {
    await localStorage.setItem('storeId', store?.storeId);
    dispatch(storeActions.selectStore(store));

    return Promise.resolve();
  };

  const getUser = async () => {
    const accessToken = window.localStorage.getItem('accessToken');

    const response = await axios.get(`${baseURL}/admin/user`, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (response.data) {
      return response.data?.result;
    }
    return null;
  };

  const register = async ({ email, password, firstName, lastName, phoneNumber, role }) => {
    const response = await axios.post('signup', {
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      role: role || USER_ROLE
    });
    if (response?.data?.status === 'success') {
      const { data } = response.data;
      const user = { ...data };
      delete user.loginToken;
      user.displayName = `${user.firstName} ${user.lastName}`;
      setSession(data.loginToken);
      dispatch({
        type: 'REGISTER',
        payload: {
          user
        }
      });
    }
  };

  const sellerRegister = async ({ userName, email, phoneNumber, state, city, comment }) => {
    const response = await axios.post(`${baseURL}/admin/sellerRegister`, {
      userName,
      email,
      state,
      city,
      phoneNumber,
      comment
    });
    if (response?.data?.result) {
      return true;
    }
    return false;
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const updatePassword = async (password) => {
    const response = await axios.post(`${baseURL}/admin/update-password`, { password });

    if (response?.data?.result) {
      await logout();
      return true;
    }
    return false;
  };

  const sendOtp = async (phoneNumber, channel) =>
    axios.post('sendOtp', {
      phoneNumber,
      channel: channel || ''
    });
  const verifyOtp = async ({ code, phoneNumber }) =>
    axios.post('verifyOtp', {
      code,
      phoneNumber
    });
  const resetPassword = () => {};

  const updateProfile = () => {};

  const updateSignupData = (signupData) => {
    dispatch({
      type: 'SIGNUP_DATA',
      payload: {
        signupData
      }
    });
  };

  const updateUserActivity = (userActivity) => {
    dispatch({
      type: 'USER_ACTIVITY',
      payload: {
        userActivity
      }
    });
  };

  const markUserVerified = async (userData) => {
    const response = await axios.post('markVerified', {
      userId: userData._id
    });
    if (response?.data?.status === 'success') {
      dispatch({
        type: 'MARK_VERIFIED'
      });
    }
  };

  const setBackLoading = (isInitialized) => {
    dispatch({
      type: 'TOGGLE_INITIALIZE',
      payload: isInitialized
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        updatePassword,
        updateProfile,
        updateSignupData,
        updateUserActivity,
        sendOtp,
        verifyOtp,
        sellerRegister,
        getUser,
        markUserVerified,
        setBackLoading,
        selectStore
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
